import { useState, useEffect } from "react";
import { Check, X } from "lucide-react";
import { PickupPicker, defaultPickupDate, WINDOWS } from "../components/PickupPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtMMDDYY(iso) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[2]}/${m[3]}/${m[1]}`;
}

export default function ReschedulePage() {
  const token = window.location.pathname.split("/reschedule/")[1] || "";
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pickupDate, setPickupDate] = useState(defaultPickupDate());
  const [pickupWindow, setPickupWindow] = useState(WINDOWS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/reschedule/${token}`);
        if (!res.ok) { setNotFound(true); return; }
        const data = await res.json();
        setBooking(data);
        if (data.pickup_date) setPickupDate(data.pickup_date);
        if (data.pickup_window) setPickupWindow(data.pickup_window);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/reschedule/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pickup_date: pickupDate, pickup_window: pickupWindow, base_url: window.location.origin }),
      });
      if (res.ok) setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="grain flex min-h-screen items-center justify-center bg-canvas px-5 py-12">
      <div className="w-full max-w-lg" data-testid="reschedule-page">{children}</div>
    </div>
  );

  if (loading) return <Shell><p className="text-center text-charcoal/45">Loading your booking…</p></Shell>;

  if (notFound) return (
    <Shell>
      <div className="rounded-[2rem] bg-white/70 p-9 text-center" data-testid="reschedule-notfound">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-stone/60 text-charcoal"><X size={24} /></span>
        <h1 className="mt-5 font-display text-3xl text-charcoal">Booking not found</h1>
        <p className="mt-2 text-charcoal/55">This link may be incorrect or expired. Call us at 617-717-9852 and we'll help.</p>
      </div>
    </Shell>
  );

  if (done) return (
    <Shell>
      <div className="rounded-[2rem] bg-white/70 p-9 text-center" data-testid="reschedule-done">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage text-white"><Check size={32} /></span>
        <h1 className="mt-6 font-display text-3xl text-charcoal sm:text-4xl">All set{booking?.name ? `, ${booking.name.split(" ")[0]}` : ""}.</h1>
        <p className="mt-3 text-charcoal/60">Your pickup has been moved. We've sent an updated confirmation. See you then!</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="overflow-hidden rounded-[2rem] bg-white/70 shadow-[0_40px_120px_-40px_rgba(23,23,23,0.4)]">
        <div className="bg-violet px-8 py-6">
          <span className="font-logo font-semibold text-3xl lowercase text-white">milla</span>
        </div>
        <div className="px-7 py-8 sm:px-9">
          <h1 className="font-display text-3xl tracking-tight text-charcoal sm:text-4xl">
            Hi{booking.name ? ` ${booking.name.split(" ")[0]}` : ""}, pick a new time.
          </h1>
          {booking.pickup_date && (
            <p className="mt-3 text-charcoal/60" data-testid="reschedule-current">
              Currently scheduled for <strong className="text-charcoal">{fmtMMDDYY(booking.pickup_date)}{booking.pickup_window ? ` · ${booking.pickup_window}` : ""}</strong>. Choose a new day and window below.
            </p>
          )}

          <div className="mt-7">
            <p className="text-sm font-medium text-charcoal/70">Choose a new pickup day &amp; window</p>
            <div className="mt-3">
              <PickupPicker date={pickupDate} window={pickupWindow} onDate={setPickupDate} onWindow={setPickupWindow} accent="sage" />
            </div>
          </div>

          <button onClick={submit} disabled={submitting} data-testid="reschedule-submit-btn"
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-violet px-8 py-4 text-base font-semibold text-white transition-all enabled:hover:bg-violet-deep enabled:hover:-translate-y-0.5 disabled:opacity-60">
            {submitting ? "Updating…" : "Confirm new time"}
          </button>
          <p className="mt-3 text-center text-xs text-charcoal/45">Questions? Call 617-717-9852.</p>
        </div>
      </div>
    </Shell>
  );
}
